import { useCallback, useEffect, useRef, useState } from 'react';
import { getJob } from './importsApi';

export const POLL_MS = 2000;
export const FINISHED = ['done', 'failed', 'cancelled'];

/**
 * A background job polled every 2 s while the page is visible and the job is
 * not over (phase 4 §6.1) — one request at a time, none while the tab is
 * hidden (it asks again on return), none once it ended: no storm.
 * `initial`: the job as last received (from the start answer).
 */
export function useJob(jobId, initial = null, { onFinished } = {}) {
  const [job, setJob] = useState(initial);
  const [error, setError] = useState(null);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;
  const doneRef = useRef(initial ? FINISHED.includes(initial.status) : false);
  const inFlight = useRef(false);   // one request at a time: a return to the tab never doubles a slow one

  const refresh = useCallback(async () => {
    if (!jobId || inFlight.current) return null;
    inFlight.current = true;
    try {
      const next = await getJob(jobId);
      setJob(next);
      setError(null);
      if (FINISHED.includes(next.status) && !doneRef.current) {
        doneRef.current = true;
        if (finishedRef.current) finishedRef.current(next);
      }
      return next;
    } catch (e) {
      setError(e);
      if (e && e.response && e.response.status === 404) doneRef.current = true;   // gone (deleted, or no longer ours): stop
      return null;
    } finally {
      inFlight.current = false;
    }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return undefined;
    let timer = null;
    let stopped = false;
    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
    const tick = async () => {
      timer = null;
      if (stopped || doneRef.current || !visible()) return;
      await refresh();
      if (!stopped && !doneRef.current && visible()) timer = setTimeout(tick, POLL_MS);
    };
    const onVisibility = () => {
      if (visible() && !timer && !doneRef.current) tick();
      if (!visible() && timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    if (!doneRef.current) timer = setTimeout(tick, initial ? POLL_MS : 0);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [jobId, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  return { job, error, refresh };
}
